import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Input, message, Radio, Space } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import { Outlet, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import AddEmailTemplate from '../../components/AddEmailTemplate';
import EmailTemplateTable from '../../components/EmailTemplateTable';
import Filter from '../../components/Filter';
import SearchParam from '../../components/SearchParam';
import { withRouteChange } from '../../services/APM';
import { EmailCommunicationTemplateCollectionParams } from '../../types';

const templateTypes = [
  { label: 'Comunicação', value: 'COMMUNICATION' },
  { label: 'Testes Mindsight', value: 'TESTS' },
  { label: 'Testes customizados', value: 'CUSTOM_TESTS' },
  { label: 'Perguntas de pré-requisito', value: 'ADDITIONAL_QUESTIONS' },
  { label: 'Mensagem boas-vindas', value: 'WELCOME_MESSAGE' },
];

const EmailTemplates = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddEmailTemplateOpen, setIsAddEmailTemplateOpen] = useState(false);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const emailTemplates = useAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type:
          (searchParams.get('type') as EmailCommunicationTemplateCollectionParams['type']) ??
          undefined,
        individual: false,
        page: parseInt(searchParams.get('page') || '1', 10),
        page_size: parseInt(searchParams.get('pageSize') || '10', 10),
        ordering: 'name',
        ...(searchParams.get('search') ? { search: searchParams.get('search') as string } : {}),
      },
    },
  ]);

  return (
    <>
      <Helmet>
        <title>Modelos de Email | Configurações | Recrutamento e Seleção</title>
      </Helmet>
      <Outlet />
      <AddEmailTemplate
        companyFromName={config.data?.companyFromName}
        enableAlchemerIntegration={config.data?.enableAlchemerIntegration}
        open={isAddEmailTemplateOpen}
        onSuccess={() => {
          message.success('Modelo adicionado.');
          queryClient.invalidateQueries('/email-communication-templates/');
          queryClient.invalidateQueries(`/configs/tenant-configurations/`);
          setIsAddEmailTemplateOpen(false);
        }}
        onClose={() => setIsAddEmailTemplateOpen(false)}
        destroyOnClose
      />
      <Filter>
        <Col flex={1}>
          <SearchParam name="search" onSetSearchParams={{ page: '1' }}>
            <Input.Search allowClear placeholder="Nome do modelo" />
          </SearchParam>
        </Col>
        <Filter.Item
          alwaysOn
          label="Tipo"
          name="type"
          selected={
            <Filter.Selection name="type" onSetSearchParams={{ page: '1' }}>
              {templateTypes.map(({ label, value }) => (
                <Filter.Tag defaultValue={value} key={value}>
                  {label}
                </Filter.Tag>
              ))}
            </Filter.Selection>
          }
        >
          <Space direction="vertical">
            <SearchParam name="type" onSetSearchParams={{ page: '1' }}>
              <Radio.Group>
                <Space direction="vertical">
                  {templateTypes.map(({ label, value }) => (
                    <Radio value={value} key={value}>
                      {label}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </SearchParam>
            <SearchParam
              array
              name="type"
              onSetSearchParams={{ page: '1' }}
              valuePropName="disabledValue"
            >
              <Filter.Button>Limpar</Filter.Button>
            </SearchParam>
          </Space>
        </Filter.Item>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddEmailTemplateOpen(true)}
          >
            Novo modelo
          </Button>
        </Col>
      </Filter>
      <EmailTemplateTable
        loading={{ spinning: emailTemplates.isFetching, size: 'large' }}
        templateTypes={templateTypes}
        dataSource={emailTemplates.data?.results ?? []}
        pagination={{
          total: emailTemplates.data?.count,
          onChange: (page, pageSize) => {
            searchParams.set('page', page.toString());
            searchParams.set('pageSize', pageSize.toString());
            setSearchParams(searchParams);
          },
          current: parseInt(searchParams.get('page') || '1', 10),
          pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        }}
        activeWelcomeTemplate={config.data?.companyWelcomeMessageTemplateId}
        searchParams={searchParams}
      />
    </>
  );
};

export default withRouteChange()(EmailTemplates);
